import { Alert, Button, Loader, Select } from '@mantine/core';
import { useShallow } from 'zustand/react/shallow';

import { ChainMainPractitionerSelect } from '@/components/chain-booking/chain-main-practitioner-select.tsx';
import { ChainPlanningSelector } from '@/components/chain-booking/chain-planning-selector.tsx';
import { ChainStepRow } from '@/components/chain-booking/chain-step-row.tsx';
import Heading4 from '@/components/text/Heading4.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { useAutoSelectSoleOption } from '@/hooks/use-auto-select-sole-option.ts';
import { useChainMainPractitionerOptions } from '@/hooks/use-chain-main-practitioner-options.ts';
import { useEnrichedPractitionerData } from '@/hooks/useEnrichedPractitionerData.ts';

import type { InsuranceType } from '@/models/insurance.ts';

import { MIN_STEPS } from '@/schemas/ChainConfigForm.schema.ts';
import type { AppointmentChainConfiguration } from '@/schemas/admin/appointment-chain-configurations.schema.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';

import { getEffectiveGap } from '@/utils/appointmentUtils.ts';
import { getActiveMarkedSteps, getEligibleMainPractitioners } from '@/utils/chain-main-practitioner-utils.ts';
import {
    buildAppointmentTypeDetailMap,
    buildCalendarOptionsByAptType,
    filterChainConfigs,
} from '@/utils/practitioner/bookingFormOptionsUtils.ts';
import { filterEnrichedDataByPracticeId } from '@/utils/practitioner/cachedDataUtils.ts';

interface Props {
    practiceId?: string;
    instanceId: string;
    insuranceType?: InsuranceType;
    chainConfigs: AppointmentChainConfiguration[];
    isChainsLoading: boolean;
    chainsError: Error | undefined;
    refetchChains: () => Promise<AppointmentChainConfiguration[] | undefined>;
}

export const ChainBookingConfigurator = ({
    practiceId,
    instanceId,
    insuranceType,
    chainConfigs,
    isChainsLoading,
    chainsError,
    refetchChains,
}: Props) => {
    const { data: practitionerData, is_loading: isPractitionerLoading } = useEnrichedPractitionerData();

    const {
        chain_id: chainId,
        removed_orders: removedOrders,
        calendar_selections: calendarSelections,
        main_practitioner_id: mainPractitionerId,
        is_chain_cleared_by_patient_type: isChainClearedByPatientType,
    } = useBookingStore(useShallow((state) => state.chain_selection));
    const isReturning = useBookingStore((state) => state.is_returning_patient);
    const setChain = useBookingStore((state) => state.setChain);
    const toggleChainStepRemoval = useBookingStore((state) => state.toggleChainStepRemoval);
    const setChainCalendar = useBookingStore((state) => state.setChainCalendar);
    const setChainMainPractitioner = useBookingStore((state) => state.setChainMainPractitioner);

    const dataFilteredByPractice = practitionerData
        ?.filter((d) => !instanceId || d.instance_id === instanceId)
        .map((d) => filterEnrichedDataByPracticeId(d, practiceId))
        .filter((d) => d !== undefined);

    const appointmentTypeDetails = buildAppointmentTypeDetailMap(dataFilteredByPractice);
    const availableChains = filterChainConfigs(chainConfigs, { practiceId, instanceId, insuranceType, isReturning });

    const selectedChain = availableChains.find((c) => c.id === chainId);
    const allOriginalOrders = selectedChain?.steps.map((s) => s.order) ?? [];
    const visibleSteps = selectedChain?.steps.filter((s) => !removedOrders.includes(s.order)) ?? [];
    const calendarOptionsByAptType = buildCalendarOptionsByAptType(dataFilteredByPractice);

    const chainOptions = availableChains.map((c) => ({
        value: c.id,
        label: `${c.name.de} (${c.steps.length} Schritte)`,
    }));

    const {
        options: mainPractitionerOptions,
        hasActiveMarkedSteps,
        isEligibleSelection,
        mainPractitionerCalendarIdsFor,
    } = useChainMainPractitionerOptions({ config: selectedChain, removedOrders: removedOrders });
    const isMainPractitionerSelectionValid = isEligibleSelection(mainPractitionerId);
    const effectiveMainPractitionerId = isMainPractitionerSelectionValid ? mainPractitionerId : undefined;
    const showReselectPrompt = hasActiveMarkedSteps && !!mainPractitionerId && !isMainPractitionerSelectionValid;
    const mainPracStepOrders = selectedChain
        ? new Set(getActiveMarkedSteps(selectedChain, removedOrders).map((s) => s.order))
        : new Set<number>();

    const autoSelectSoleMainPractitioner = (chain: AppointmentChainConfiguration, orders: number[]): void => {
        const eligible = getEligibleMainPractitioners(chain, orders);
        if (eligible.length === 1) setChainMainPractitioner(eligible[0].practitioner_id);
    };

    const handleChainSelect = (value: string | null): void => {
        setChain(value ?? undefined, insuranceType);
        if (!value) return;
        const chain = availableChains.find((c) => c.id === value);
        if (!chain) return;
        autoSelectSoleMainPractitioner(chain, []);
    };

    const isLoading = isChainsLoading || isPractitionerLoading;

    useAutoSelectSoleOption({
        options: chainOptions,
        getKey: (option) => option.value,
        hasSelection: chainId !== undefined,
        onSelect: (option) => handleChainSelect(option.value),
        isEnabled: !isLoading && !chainsError,
    });

    const dropInvalidMarkedCalendars = (nextPractitionerId: string | undefined): void => {
        if (!selectedChain) return;
        for (const step of getActiveMarkedSteps(selectedChain, removedOrders)) {
            const selected = calendarSelections[step.order];
            if (!selected) continue;
            const allowed = nextPractitionerId
                ? mainPractitionerCalendarIdsFor(step.order, nextPractitionerId)
                : new Set<string>();
            if (!allowed.has(selected)) setChainCalendar(step.order, undefined);
        }
    };

    const handleMainPractitionerChange = (nextPractitionerId: string | undefined): void => {
        dropInvalidMarkedCalendars(nextPractitionerId);
        setChainMainPractitioner(nextPractitionerId);
    };

    const onStepRemoval = (order: number): void => {
        toggleChainStepRemoval(order);
        if (!selectedChain) return;
        if (!mainPractitionerId) {
            const nextRemovedOrders = useBookingStore.getState().chain_selection.removed_orders;
            autoSelectSoleMainPractitioner(selectedChain, nextRemovedOrders);
        }
    };

    const tooFewSteps = visibleSteps.length < MIN_STEPS;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader />
            </div>
        );
    }

    const handleRetryChains = (): void => {
        void refetchChains();
    };

    if (chainsError) {
        return (
            <div className="flex flex-col items-center gap-3 py-8">
                <Paragraph className="text-sm text-red-500">Ketten konnten nicht geladen werden.</Paragraph>
                <Button size="sm" variant="default" onClick={handleRetryChains}>
                    Erneut versuchen
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {isChainClearedByPatientType && (
                <Alert color="yellow" variant="light">
                    Patiententyp geändert. Bitte Terminkette erneut wählen.
                </Alert>
            )}

            <Select
                key={chainId ?? 'empty'}
                label="Terminkette"
                placeholder="Kette auswählen..."
                searchable
                data={chainOptions}
                value={chainId}
                allowDeselect={false}
                onChange={handleChainSelect}
            />

            {!selectedChain && <ChainPlanningSelector />}

            {selectedChain && (
                <>
                    <div className="flex items-start justify-between gap-4">
                        <Heading4 className="text-base font-semibold">Termine & Behandelnde</Heading4>
                        {hasActiveMarkedSteps && (
                            <ChainMainPractitionerSelect
                                options={mainPractitionerOptions}
                                value={effectiveMainPractitionerId}
                                onChange={handleMainPractitionerChange}
                                reselectPrompt={showReselectPrompt}
                            />
                        )}
                    </div>

                    {tooFewSteps && (
                        <Paragraph className="text-sm text-red-500">
                            Mindestens {MIN_STEPS} Schritte sind erforderlich.
                        </Paragraph>
                    )}

                    <div className="flex flex-col">
                        {visibleSteps.map((step, index) => {
                            const aptType = appointmentTypeDetails[step.appointment_type_id];
                            const nextStep = visibleSteps[index + 1];
                            const effectiveGap = nextStep
                                ? getEffectiveGap(selectedChain.gaps, step.order, nextStep.order, allOriginalOrders)
                                : undefined;
                            const stepCalendarOptions = calendarOptionsByAptType[step.appointment_type_id] ?? [];
                            const isMarkedStep = mainPracStepOrders.has(step.order);
                            const mainPracCalendarIds =
                                isMarkedStep && effectiveMainPractitionerId
                                    ? mainPractitionerCalendarIdsFor(step.order, effectiveMainPractitionerId)
                                    : undefined;
                            const calendarOptions = mainPracCalendarIds
                                ? stepCalendarOptions.filter((option) => mainPracCalendarIds.has(option.value))
                                : stepCalendarOptions;
                            const handleCalendarChange = (calendarId: string | undefined): void => {
                                setChainCalendar(step.order, calendarId);
                            };
                            const handleStepRemove = (): void => onStepRemoval(step.order);

                            return (
                                <ChainStepRow
                                    key={`${chainId}-${step.order}`}
                                    chainId={chainId}
                                    stepOrder={step.order}
                                    appointmentTypeName={aptType?.name ?? 'Unbekannte Terminart'}
                                    durationMinutes={aptType?.durationMinutes}
                                    calendarOptions={calendarOptions}
                                    selectedCalendarId={calendarSelections[step.order]}
                                    isMarked={isMarkedStep}
                                    isRemovable={visibleSteps.length > MIN_STEPS}
                                    effectiveGap={effectiveGap}
                                    onCalendarChange={handleCalendarChange}
                                    onRemove={handleStepRemove}
                                />
                            );
                        })}
                    </div>

                    <ChainPlanningSelector />
                </>
            )}
        </div>
    );
};
